import { PropsWithChildren, useMemo } from 'react';
import { ControlledShow } from 'framework/show';
import { ProjectSecurityContext } from 'domain/shared/newProject';

import { Loading, LoaderSize } from 'framework/ui/mui-builder';
import { useCriticalPeriodById } from '../team/Show/ProjectCriticalPeriods/hooks/useCriticalPeriods';
import { PermissionsMap, SecurityContextTemplate, useGlobalSecurityReactContext } from 'framework/authorization';
import { useParams } from 'react-router-dom';

export const CriticalPeriodContext = ({
  permissionToCheck,
  children,
}: PropsWithChildren<{
  permissionToCheck: string | string[];
}>) => {
  const { id, criticalPeriodId } = useParams<Dictionary<string>>();
  const { criticalPeriod, accessInfo, loading } = useCriticalPeriodById(id!, criticalPeriodId!);
  const globalContext = useGlobalSecurityReactContext() || {};

  const { contextValue, projectPermissionsInGlobalFormat, projectPermissions } = useMemo(() => {
    if (!criticalPeriod) {
      return {
        contextValue: null,
        projectPermissionsInGlobalFormat: [],
      };
    }

    const permissionsGlobalFormat =
      accessInfo?.sections.flatMap(({ section, allowedActions }) =>
        allowedActions.map(permission => `GRAPHQL__${section}__${permission}`),
      ) || [];

    return {
      contextValue: criticalPeriod,
      projectPermissionsInGlobalFormat: permissionsGlobalFormat,
      projectPermissions: accessInfo,
    };
  }, [criticalPeriod, accessInfo]);

  if (!criticalPeriod || loading) {
    return <Loading isInitialPosition size={LoaderSize.Small} />;
  }

  return (
    <SecurityContextTemplate
      id={id}
      getPermissionsFunc={() =>
        Promise.all([
          Promise.resolve(Object.keys(globalContext.permissions as unknown as PermissionsMap) || []),
          Promise.resolve(projectPermissionsInGlobalFormat || []),
        ]).then(permissions => {
          return permissions.reduce((flatten, arr) => [...flatten, ...arr]);
        })
      }
      contextName={'CriticalPeriodContextSecurity'}
      permissionToCheck={permissionToCheck}
    >
      <ProjectSecurityContext permissions={projectPermissions}>
        <ControlledShow data={contextValue}>{children}</ControlledShow>
      </ProjectSecurityContext>
    </SecurityContextTemplate>
  );
};
